/** @jsxImportSource @emotion/react */
import React from 'react'
import {
    Area,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { MonthlySalesBucket } from '../orders/models/dashboard-stats.type'
import { CHART_COLORS, formatCurrency, formatInt, formatMonthLabel } from './dashboard-helpers'
import { DashboardChartCard } from './dashboard-chart-card'

type Props = {
    data: MonthlySalesBucket[]
}

export const SalesOverTimeChart: React.FC<Props> = ({ data }) => {
    const { t } = useTranslation()
    const reportsLabel = t('TEXT_TOTAL_REPORTS', 'Rapporter levert')
    const revenueLabel = t('TEXT_TOTAL_REVENUE', 'Omsetning')

    const chartData = data.map((bucket) => ({
        label: formatMonthLabel(bucket.label),
        items: bucket.items,
        revenue: bucket.revenue,
    }))

    return (
        <DashboardChartCard
            title={t('TEXT_SALES_OVER_TIME', 'Salg over tid')}
            isEmpty={chartData.length === 0}
            emptyText={t('TEXT_NO_DATA', 'Ingen data for valgt periode')}
        >
            <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} allowDecimals={false} />
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value: number) => formatInt(value)}
                    />
                    <Tooltip
                        formatter={(value: number, name: string) =>
                            name === revenueLabel ? formatCurrency(value) : formatInt(value)
                        }
                    />
                    <Legend />
                    <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="items"
                        name={reportsLabel}
                        stroke={CHART_COLORS[0]}
                        fill={CHART_COLORS[0]}
                        fillOpacity={0.15}
                    />
                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="revenue"
                        name={revenueLabel}
                        stroke={CHART_COLORS[1]}
                        strokeWidth={2}
                        dot={false}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </DashboardChartCard>
    )
}
